import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CMSContext = createContext(null);

export const useCMS = () => {
  const context = useContext(CMSContext);
  if (!context) {
    throw new Error('useCMS must be used within a CMSProvider');
  }
  return context;
};

export const CMSProvider = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [adminUser, setAdminUser] = useState(JSON.parse(localStorage.getItem('admin_user') || 'null'));
  const [loading, setLoading] = useState(true);

  // Verify session on mount
  useEffect(() => {
    const verifySession = async () => {
      if (token) {
        try {
          const res = await fetch(`${API_URL}/api/admin/verify`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            setIsAdmin(true);
            if (data.user) {
              setAdminUser(data.user);
              localStorage.setItem('admin_user', JSON.stringify(data.user));
            }
          } else {
            localStorage.removeItem('admin_token');
            localStorage.removeItem('admin_user');
            setToken(null);
            setAdminUser(null);
            setIsAdmin(false);
          }
        } catch (e) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          setToken(null);
          setAdminUser(null);
          setIsAdmin(false);
        }
      }
      setLoading(false);
    };
    verifySession();
  }, [token]);

  const login = async (email, password) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();
      if (data.token) {
        localStorage.setItem('admin_token', data.token);
        localStorage.setItem('admin_user', JSON.stringify(data.user));
        setToken(data.token);
        setAdminUser(data.user);
        setIsAdmin(true);
        return { success: true };
      }
      return { success: false, message: data.detail || 'Invalid credentials' };
    } catch (e) {
      return { success: false, message: 'Login failed' };
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/admin/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) {}
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    setToken(null);
    setAdminUser(null);
    setIsAdmin(false);
  };

  // Check if user has permission for a section
  const hasPermission = (section, level = 'view') => {
    if (!adminUser) return false;
    if (adminUser.role === 'super_admin') return true;
    
    const permissions = adminUser.permissions || {};
    const userLevel = permissions[section] || 'none';
    
    const levels = { none: 0, view: 1, edit: 2 };
    return (levels[userLevel] || 0) >= (levels[level] || 1);
  };

  // Check if user is super admin
  const isSuperAdmin = () => {
    return adminUser?.role === 'super_admin';
  };

  // Generic API call with auth
  const apiCall = useCallback(async (endpoint, options = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    
    const res = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers
    });
    
    if (!res.ok) {
      const error = await res.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'Request failed');
    }
    
    return res.json();
  }, [token]);

  // CMS Data fetchers (public)
  const getHeroContent = useCallback(() => apiCall('/api/cms/hero'), [apiCall]);
  const getTestimonials = useCallback((activeOnly = true) => 
    apiCall(`/api/cms/testimonials?active_only=${activeOnly}`), [apiCall]);
  const getFeatures = useCallback((activeOnly = true, category = null) => {
    let url = `/api/cms/features?active_only=${activeOnly}`;
    if (category) url += `&category=${category}`;
    return apiCall(url);
  }, [apiCall]);
  const getPricing = useCallback((activeOnly = true) => 
    apiCall(`/api/cms/pricing?active_only=${activeOnly}`), [apiCall]);
  const getBlogPosts = useCallback((publishedOnly = true, category = null, limit = 20) => {
    let url = `/api/cms/blog?published_only=${publishedOnly}&limit=${limit}`;
    if (category) url += `&category=${category}`;
    return apiCall(url);
  }, [apiCall]);
  const getBlogPost = useCallback((slug) => apiCall(`/api/cms/blog/${slug}`), [apiCall]);
  const getPageSections = useCallback((page, activeOnly = true) => 
    apiCall(`/api/cms/sections/${page}?active_only=${activeOnly}`), [apiCall]);

  // About Page CMS
  const getAboutSections = useCallback((activeOnly = true) => 
    apiCall(`/api/cms/about?active_only=${activeOnly}`), [apiCall]);
  const getAboutSection = useCallback((sectionType) => 
    apiCall(`/api/cms/about/${sectionType}`), [apiCall]);
  
  // Solutions Page CMS
  const getSolutions = useCallback((activeOnly = true) => 
    apiCall(`/api/cms/solutions?active_only=${activeOnly}`), [apiCall]);
  
  // FAQs CMS
  const getFAQs = useCallback((activeOnly = true, category = null) => {
    let url = `/api/cms/faqs?active_only=${activeOnly}`;
    if (category) url += `&category=${category}`;
    return apiCall(url);
  }, [apiCall]);

  // CMS Data updaters (admin only)
  const updateHeroContent = useCallback((data) => 
    apiCall('/api/cms/hero', { method: 'PUT', body: JSON.stringify(data) }), [apiCall]);
  
  const createTestimonial = useCallback((data) => 
    apiCall('/api/cms/testimonials', { method: 'POST', body: JSON.stringify(data) }), [apiCall]);
  const updateTestimonial = useCallback((id, data) => 
    apiCall(`/api/cms/testimonials/${id}`, { method: 'PUT', body: JSON.stringify(data) }), [apiCall]);
  const deleteTestimonial = useCallback((id) => 
    apiCall(`/api/cms/testimonials/${id}`, { method: 'DELETE' }), [apiCall]);

  const createFeature = useCallback((data) => 
    apiCall('/api/cms/features', { method: 'POST', body: JSON.stringify(data) }), [apiCall]);
  const updateFeature = useCallback((id, data) => 
    apiCall(`/api/cms/features/${id}`, { method: 'PUT', body: JSON.stringify(data) }), [apiCall]);
  const deleteFeature = useCallback((id) => 
    apiCall(`/api/cms/features/${id}`, { method: 'DELETE' }), [apiCall]);

  const createPricing = useCallback((data) => 
    apiCall('/api/cms/pricing', { method: 'POST', body: JSON.stringify(data) }), [apiCall]);
  const updatePricing = useCallback((id, data) => 
    apiCall(`/api/cms/pricing/${id}`, { method: 'PUT', body: JSON.stringify(data) }), [apiCall]);
  const deletePricing = useCallback((id) => 
    apiCall(`/api/cms/pricing/${id}`, { method: 'DELETE' }), [apiCall]);

  const createBlogPost = useCallback((data) => 
    apiCall('/api/cms/blog', { method: 'POST', body: JSON.stringify(data) }), [apiCall]);
  const updateBlogPost = useCallback((id, data) => 
    apiCall(`/api/cms/blog/${id}`, { method: 'PUT', body: JSON.stringify(data) }), [apiCall]);
  const deleteBlogPost = useCallback((id) => 
    apiCall(`/api/cms/blog/${id}`, { method: 'DELETE' }), [apiCall]);

  const createPageSection = useCallback((data) => 
    apiCall('/api/cms/sections', { method: 'POST', body: JSON.stringify(data) }), [apiCall]);
  const updatePageSection = useCallback((id, data) => 
    apiCall(`/api/cms/sections/${id}`, { method: 'PUT', body: JSON.stringify(data) }), [apiCall]);
  const deletePageSection = useCallback((id) => 
    apiCall(`/api/cms/sections/${id}`, { method: 'DELETE' }), [apiCall]);

  // About Page Admin
  const createAboutSection = useCallback((data) => 
    apiCall('/api/cms/about', { method: 'POST', body: JSON.stringify(data) }), [apiCall]);
  const updateAboutSection = useCallback((id, data) => 
    apiCall(`/api/cms/about/${id}`, { method: 'PUT', body: JSON.stringify(data) }), [apiCall]);
  const deleteAboutSection = useCallback((id) => 
    apiCall(`/api/cms/about/${id}`, { method: 'DELETE' }), [apiCall]);

  // Solutions Admin
  const createSolution = useCallback((data) => 
    apiCall('/api/cms/solutions', { method: 'POST', body: JSON.stringify(data) }), [apiCall]);
  const updateSolution = useCallback((id, data) => 
    apiCall(`/api/cms/solutions/${id}`, { method: 'PUT', body: JSON.stringify(data) }), [apiCall]);
  const deleteSolution = useCallback((id) => 
    apiCall(`/api/cms/solutions/${id}`, { method: 'DELETE' }), [apiCall]);

  // FAQs Admin
  const createFAQ = useCallback((data) => 
    apiCall('/api/cms/faqs', { method: 'POST', body: JSON.stringify(data) }), [apiCall]);
  const updateFAQ = useCallback((id, data) => 
    apiCall(`/api/cms/faqs/${id}`, { method: 'PUT', body: JSON.stringify(data) }), [apiCall]);
  const deleteFAQ = useCallback((id) => 
    apiCall(`/api/cms/faqs/${id}`, { method: 'DELETE' }), [apiCall]);

  // Feature Packages (for Features & Pricing page)
  const getFeaturePackages = useCallback((activeOnly = true) => 
    apiCall(`/api/cms/feature-packages?active_only=${activeOnly}`), [apiCall]);
  const createFeaturePackage = useCallback((data) => 
    apiCall('/api/cms/feature-packages', { method: 'POST', body: JSON.stringify(data) }), [apiCall]);
  const updateFeaturePackage = useCallback((id, data) => 
    apiCall(`/api/cms/feature-packages/${id}`, { method: 'PUT', body: JSON.stringify(data) }), [apiCall]);
  const deleteFeaturePackage = useCallback((id) => 
    apiCall(`/api/cms/feature-packages/${id}`, { method: 'DELETE' }), [apiCall]);

  // Feature Comparison Sections (accordion sections)
  const getFeatureSections = useCallback((activeOnly = true) => 
    apiCall(`/api/cms/feature-sections?active_only=${activeOnly}`), [apiCall]);
  const createFeatureSection = useCallback((data) => 
    apiCall('/api/cms/feature-sections', { method: 'POST', body: JSON.stringify(data) }), [apiCall]);
  const updateFeatureSection = useCallback((id, data) => 
    apiCall(`/api/cms/feature-sections/${id}`, { method: 'PUT', body: JSON.stringify(data) }), [apiCall]);
  const deleteFeatureSection = useCallback((id) => 
    apiCall(`/api/cms/feature-sections/${id}`, { method: 'DELETE' }), [apiCall]);

  // Feature Categories (tab categories)
  const getFeatureCategories = useCallback((activeOnly = true) => 
    apiCall(`/api/cms/feature-categories?active_only=${activeOnly}`), [apiCall]);
  const createFeatureCategory = useCallback((data) => 
    apiCall('/api/cms/feature-categories', { method: 'POST', body: JSON.stringify(data) }), [apiCall]);
  const updateFeatureCategory = useCallback((id, data) => 
    apiCall(`/api/cms/feature-categories/${id}`, { method: 'PUT', body: JSON.stringify(data) }), [apiCall]);
  const deleteFeatureCategory = useCallback((id) => 
    apiCall(`/api/cms/feature-categories/${id}`, { method: 'DELETE' }), [apiCall]);

  // Features & Pricing Page Settings
  const getFPPageSettings = useCallback(() => 
    apiCall('/api/cms/fp-page-settings'), [apiCall]);
  const updateFPPageSettings = useCallback((data) => 
    apiCall('/api/cms/fp-page-settings', { method: 'PUT', body: JSON.stringify(data) }), [apiCall]);

  // Social Media Links
  const getSocialLinks = useCallback(() => 
    apiCall('/api/cms/social-links'), [apiCall]);
  const updateSocialLinks = useCallback((data) => 
    apiCall('/api/cms/social-links', { method: 'PUT', body: JSON.stringify(data) }), [apiCall]);

  // ROI Calculator Settings
  const getROISettings = useCallback(() => 
    apiCall('/api/cms/roi-settings'), [apiCall]);
  const updateROISettings = useCallback((data) => 
    apiCall('/api/cms/roi-settings', { method: 'PUT', body: JSON.stringify(data) }), [apiCall]);

  // Contact Page Settings
  const getContactSettings = useCallback(() => 
    apiCall('/api/cms/contact-settings'), [apiCall]);
  const updateContactSettings = useCallback((data) => 
    apiCall('/api/cms/contact-settings', { method: 'PUT', body: JSON.stringify(data) }), [apiCall]);

  // AI Demo Page Settings
  const getAIDemoSettings = useCallback(() => 
    apiCall('/api/cms/ai-demo-settings'), [apiCall]);
  const updateAIDemoSettings = useCallback((data) => 
    apiCall('/api/cms/ai-demo-settings', { method: 'PUT', body: JSON.stringify(data) }), [apiCall]);

  // AI Features Page Settings
  const getAIFeaturesSettings = useCallback(() => 
    apiCall('/api/cms/ai-features-settings'), [apiCall]);
  const updateAIFeaturesSettings = useCallback((data) => 
    apiCall('/api/cms/ai-features-settings', { method: 'PUT', body: JSON.stringify(data) }), [apiCall]);

  // AI Features List
  const getAIFeatures = useCallback((activeOnly = true) => 
    apiCall(`/api/cms/ai-features?active_only=${activeOnly}`), [apiCall]);
  const createAIFeature = useCallback((data) => 
    apiCall('/api/cms/ai-features', { method: 'POST', body: JSON.stringify(data) }), [apiCall]);
  const updateAIFeature = useCallback((id, data) => 
    apiCall(`/api/cms/ai-features/${id}`, { method: 'PUT', body: JSON.stringify(data) }), [apiCall]);
  const deleteAIFeature = useCallback((id) => 
    apiCall(`/api/cms/ai-features/${id}`, { method: 'DELETE' }), [apiCall]);

  const getDashboardStats = useCallback(() => apiCall('/api/admin/dashboard'), [apiCall]);

  const value = {
    isAdmin,
    loading,
    login,
    logout,
    token,
    // Public getters
    getHeroContent,
    getTestimonials,
    getFeatures,
    getPricing,
    getBlogPosts,
    getBlogPost,
    getPageSections,
    getAboutSections,
    getAboutSection,
    getSolutions,
    getFAQs,
    getFeaturePackages,
    getFeatureSections,
    getFeatureCategories,
    getROISettings,
    getContactSettings,
    getAIDemoSettings,
    getAIFeaturesSettings,
    getAIFeatures,
    // Admin setters
    updateHeroContent,
    createTestimonial, updateTestimonial, deleteTestimonial,
    createFeature, updateFeature, deleteFeature,
    createPricing, updatePricing, deletePricing,
    createBlogPost, updateBlogPost, deleteBlogPost,
    createPageSection, updatePageSection, deletePageSection,
    createAboutSection, updateAboutSection, deleteAboutSection,
    createSolution, updateSolution, deleteSolution,
    createFAQ, updateFAQ, deleteFAQ,
    createFeaturePackage, updateFeaturePackage, deleteFeaturePackage,
    createFeatureSection, updateFeatureSection, deleteFeatureSection,
    createFeatureCategory, updateFeatureCategory, deleteFeatureCategory,
    getFPPageSettings, updateFPPageSettings,
    getSocialLinks, updateSocialLinks,
    updateROISettings,
    updateContactSettings,
    updateAIDemoSettings,
    updateAIFeaturesSettings,
    createAIFeature, updateAIFeature, deleteAIFeature,
    getDashboardStats,
    // Role-based access
    adminUser,
    hasPermission,
    isSuperAdmin
  };

  return (
    <CMSContext.Provider value={value}>
      {children}
    </CMSContext.Provider>
  );
};
